const GAPI_SCRIPT_SRC = 'https://apis.google.com/js/api.js';
const SPREADSHEET_MIME_TYPE = 'application/vnd.google-apps.spreadsheet';

type PickerCallback = (data: unknown) => unknown;

type PickerDocsView = {
  setMode: (mode: unknown) => PickerDocsView;
  setEnableDrives: (enabled: boolean) => PickerDocsView;
  setSelectFolderEnabled: (enabled: boolean) => PickerDocsView;
  setMimeTypes: (mimeTypes: string) => PickerDocsView;
};

/**
 * Handle on a built picker. Callers keep it so they can dismiss a picker the
 * user never resolved themselves — the picker owns its own overlay, so nothing
 * else can take it off screen.
 */
export type SpreadsheetPicker = {
  setVisible: (visible: boolean) => void;
};

type PickerBuilder = {
  enableFeature: (feature: unknown) => PickerBuilder;
  setAppId: (appId: string) => PickerBuilder;
  setOAuthToken: (accessToken: string) => PickerBuilder;
  setCallback: (callback: PickerCallback) => PickerBuilder;
  addView: (view: PickerDocsView) => PickerBuilder;
  build: () => SpreadsheetPicker;
};

type GooglePickerApi = {
  DocsView: new (viewId: unknown) => PickerDocsView;
  PickerBuilder: new () => PickerBuilder;
  ViewId: { SPREADSHEETS: unknown };
  DocsViewMode: { LIST: unknown };
  Feature: { SUPPORT_DRIVES: unknown; NAV_HIDDEN: unknown };
};

type GapiApi = {
  load: (api: string, options: { callback: () => void; onerror: () => void }) => void;
};

type GooglePickerWindow = Window &
  typeof globalThis & {
    gapi?: GapiApi;
    google?: { picker?: GooglePickerApi };
  };

let gapiLoadPromise: Promise<GapiApi> | null = null;
let pickerLoadPromise: Promise<GooglePickerApi> | null = null;

function googleWindow(): GooglePickerWindow {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return window as GooglePickerWindow;
}

function injectGapiScript(): Promise<GapiApi> {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.src = GAPI_SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', () => {
      const gapi = googleWindow().gapi;
      if (gapi == null) {
        reject(new Error('Google API script loaded without window.gapi'));
        return;
      }
      resolve(gapi);
    });
    script.addEventListener('error', () => reject(new Error('Unable to load Google API script')));

    document.body.appendChild(script);
  });
}

export function loadGapi(): Promise<GapiApi> {
  const existing = googleWindow().gapi;
  if (existing != null) {
    return Promise.resolve(existing);
  }

  gapiLoadPromise ??= injectGapiScript().catch((error: unknown) => {
    gapiLoadPromise = null;
    throw error;
  });

  return gapiLoadPromise;
}

export async function loadGooglePicker(): Promise<GooglePickerApi> {
  const existing = googleWindow().google?.picker;
  if (existing != null) {
    return existing;
  }

  pickerLoadPromise ??= loadGapi()
    .then(
      (gapi) =>
        new Promise<GooglePickerApi>((resolve, reject) => {
          gapi.load('picker', {
            callback: () => {
              const picker = googleWindow().google?.picker;
              if (picker == null) {
                reject(new Error('Google Picker loaded without window.google.picker'));
                return;
              }
              resolve(picker);
            },
            onerror: () => reject(new Error('Unable to load Google Picker')),
          });
        }),
    )
    .catch((error: unknown) => {
      pickerLoadPromise = null;
      throw error;
    });

  return pickerLoadPromise;
}

export type OpenSpreadsheetPickerOptions = {
  accessToken: string;
  appId: string;
  callback: PickerCallback;
};

export async function openSpreadsheetPicker({
  accessToken,
  appId,
  callback,
}: OpenSpreadsheetPickerOptions): Promise<SpreadsheetPicker> {
  const pickerApi = await loadGooglePicker();
  const view = new pickerApi.DocsView(pickerApi.ViewId.SPREADSHEETS)
    .setMode(pickerApi.DocsViewMode.LIST)
    .setEnableDrives(false)
    .setSelectFolderEnabled(false)
    .setMimeTypes(SPREADSHEET_MIME_TYPE);

  const picker = new pickerApi.PickerBuilder()
    .enableFeature(pickerApi.Feature.SUPPORT_DRIVES)
    .enableFeature(pickerApi.Feature.NAV_HIDDEN)
    .setAppId(appId)
    .setOAuthToken(accessToken)
    .setCallback(callback)
    .addView(view)
    .build();

  picker.setVisible(true);

  return picker;
}

export function resetGooglePickerLoaderForTest(): void {
  gapiLoadPromise = null;
  pickerLoadPromise = null;
}
